<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.lackStatistics'), $t('text.list')]">
      </breadcrumb>
    </div>
    <div class="search">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            v-model.trim="searchForm.serialNo"
            :placeholder="$t('material.serialNoName')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="searchForm.supplierId"
            :placeholder="$t('menu.suppliers')"
            size="small"
            filterable
            clearable
          >
            <el-option
              v-for="supplier in suppliers"
              :key="supplier.id"
              :label="supplier.name"
              :value="supplier.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="searchForm.subjectId"
            :placeholder="$t('menu.subjects')"
            size="small"
            filterable
            clearable
          >
            <el-option
              v-for="subject in subjects"
              :key="subject.id"
              :label="subject.name"
              :value="subject.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>
    <div class="wrapper table-area">
      <sticky :sticky-top="50" :viewport="1024" :is-rem="false" class="sticky">
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <el-button type="primary" size="small" @click="createProcure">{{
              $t("action.applyPurchase")
            }}</el-button>
          </template>
        </vxe-toolbar>
      </sticky>

      <vxe-table
        :data="records"
        @checkbox-change="setSelected"
        @checkbox-all="setSelected"
        :custom-config="{ storage: true }"
        :loading="loading"
        id="materialTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        size="mini"
        resizable
        round
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column field="checkbox" type="checkbox" width="35" />
        <vxe-table-column
          field="image"
          :title="$t('product.images')"
          width="80"
        >
          <template v-slot="{ row }">
            <thumbnail :images="row.image" />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="serialNo"
          :title="$t('material.serialNo')"
          min-width="100"
        >
          <template v-slot="{ row }">
            {{ row.serialNo }}
            <div class="font10">{{ row.name }} {{ row.subjectName }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="color"
          :title="$t('material.color')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.color }}
            <div class="font10">{{ row.colorNo }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="supplierName"
          :title="$t('menu.suppliers')"
          min-width="80"
        >
          <template v-slot="{ row }">
            <span v-if="row.defaultSupplier != null">
              {{ row.defaultSupplier.supplierName }}
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="requireQty"
          :title="$t('material.requireQty')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.requireQty }}{{ row.unit }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="storeQuantity"
          :title="$t('material.storeQuantity')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.inventoryQty }}{{ row.unit }}
          </template>
        </vxe-table-column>

        <vxe-table-column
          field="inventories"
          :title="$t('inventory.inventoryInfo')"
          min-width="90"
        >
          <template v-slot="{ row }">
            <span v-if="row.inventories && row.inventories.length != 0">
              <div v-for="i of row.inventories" :key="i.id" class="font10">
                {{ i.warehouseName }}({{ i.shelf }})&nbsp;{{ i.storeQuantity
                }}{{ i.material.unit }}
              </div>
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="loadingQty"
          :title="$t('material.loadingQty')"
          min-width="80"
        >
          <template v-slot="{ row }">
            <el-popover
              placement="right"
              width="700"
              trigger="click"
              @show="getOnTheWays(row)"
            >
              <vxe-table
                :data="onTheWays"
                id="onTheWaysTable"
                row-id="id"
                ref="onTheWaysTable"
                class="recordsTable"
                size="mini"
                resizable
                round
                auto-resize
                highlight-hover-row
              >
                <vxe-table-column
                  field="serialNo"
                  :title="$t('procure.serialNo')"
                  min-width="140"
                >
                  <template v-slot="{ row }">
                    <span v-if="row.procure != null">
                      {{ row.procure.serialNo }}
                    </span>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="workflowState"
                  :title="$t('text.workflowState')"
                  min-width="100"
                >
                  <template v-slot="{ row }">
                    <state-label
                      v-if="row.procure != null"
                      :state="row.procure.workflowState"
                    >
                      {{ $t("procureTypes." + row.procure.workflowState) }}
                    </state-label>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="handOverAt"
                  :title="$t('text.handOverAt')"
                  min-width="100"
                >
                  <template v-slot="{ row }">
                    <span v-if="row.procure != null">
                      {{ row.procure.handOverAt }}
                    </span>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="quantity"
                  :title="$t('text.quantity')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    {{ row.quantity }}{{ row.unit }}
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="receiveQuantity"
                  :title="$t('procure.receiveQuantity')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    {{ row.receiveQuantity }}{{ row.unit }}
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="loadingQty"
                  :title="$t('material.loadingQty')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    {{ row.quantity - row.receiveQuantity }}{{ row.unit }}
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="supplierName"
                  :title="$t('menu.suppliers')"
                  min-width="100"
                />
                <vxe-table-column
                  field="remark"
                  :title="$t('text.remark')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    <raw-html-limit-number
                      :content="row.remark"
                      :number="6"
                      type="richText"
                    />
                  </template>
                </vxe-table-column>
              </vxe-table>
              <span slot="reference" class="blue popover-reference font10">
                {{ row.loadingQty }}{{ row.unit }}
              </span>
            </el-popover>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="unQualityQty"
          :title="$t('material.unQualityQty')"
          min-width="90"
        >
          <template v-slot="{ row }">
            {{ row.unQualityQty || 0 }}{{ row.unit }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="unStorageQty"
          :title="$t('material.unStorageQty')"
          min-width="110"
        >
          <template v-slot="{ row }">
            {{ row.unStorageQty || 0 }}{{ row.unit }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="deficitQty"
          :title="$t('material.deficitQty2')"
          min-width="110"
        >
          <template v-slot="{ row }">
            <span class="red"> {{ row.deficitQty }}{{ row.unit }} </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="remark"
          :title="$t('text.remark')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <raw-html-limit-number
              :content="row.remark"
              :number="6"
              type="richText"
            />
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips">
          选中了
          <span class="strong">{{ selectRecordQty }}</span>
          条记录
        </div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>

    <el-dialog
      :title="$t('action.applyPurchase')"
      :close-on-click-modal="false"
      :visible.sync="createProcureDF"
      width="60%"
    >
      <el-form
        ref="createProcureForm"
        :model="createProcureObj"
        label-width="80px"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              :label="$t('text.handOverAt')"
              :prop="'handOverAt'"
              :rules="[
                {
                  required: true,
                  message: $t('rules.required', [$t('text.handOverAt')]),
                  trigger: 'blur',
                },
              ]"
            >
              <el-date-picker
                size="small"
                v-model="createProcureObj.handOverAt"
                type="date"
                style="width: 100%"
                :picker-options="pickerTodayAfterTime"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :prop="'remark'" :label="$t('text.remark')">
              <el-input
                size="small"
                v-model.trim="createProcureObj.remark"
                :placeholder="$t('text.remark')"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="mgb15">
          <vxe-table
            :data="createProcureObj.procureItems"
            size="mini"
            resizable
            round
            auto-resize
            highlight-hover-row
            :scroll-y="{ enabled: false }"
          >
            <vxe-table-column
              field="image"
              :title="$t('product.images')"
              width="80"
            >
              <template v-slot="{ row }">
                <thumbnail :images="row.image" />
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="serialNo"
              :title="$t('material.serialNo')"
              min-width="100"
            >
              <template v-slot="{ row }">
                {{ row.materialSerialNo }}
                <div class="font10">
                  {{ row.materialName }} {{ row.subjectName }}
                </div>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="color"
              :title="$t('material.color')"
              min-width="80"
            >
              <template v-slot="{ row }">
                {{ row.color }}
                <div class="font10">{{ row.colorNo }}</div>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="supplierId"
              :title="$t('menu.suppliers')"
              min-width="100"
            >
              <template v-slot="{ row }">
                <!-- <el-select
                  v-model="row.supplierId"
                  clearable
                  filterable
                  size="small"
                >
                  <el-option
                    v-for="supplier in suppliers"
                    :key="supplier.id"
                    :label="supplier.name"
                    :value="supplier.id"
                  />
                </el-select> -->
                <el-select
                  size="small"
                  v-model="row.supplierId"
                  filterable
                  clearable
                >
                  <el-option-group
                    v-for="group in groupProductSuppliers(row)"
                    :key="group.label"
                    :label="group.label"
                  >
                    <el-option
                      v-for="supplier in group.suppliers"
                      :key="supplier.supplierId"
                      :label="
                        supplier.price
                          ? supplier.supplierName + '(¥' + supplier.price + ')'
                          : supplier.supplierName
                      "
                      :value="supplier.supplierId"
                    ></el-option>
                  </el-option-group>
                </el-select>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="quantity"
              :title="$t('text.quantity')"
              min-width="60"
            >
              <template v-slot="{ row }">
                <el-input
                  v-model.trim="row.quantity"
                  size="small"
                  :placeholder="row.inUnit"
                >
                  <template slot="append">{{ row.inUnit }}</template>
                </el-input>
              </template>
            </vxe-table-column>

            <vxe-table-column
              field="price"
              :title="$t('material.inPrice')"
              min-width="80"
            >
              <template v-slot="{ row }">
                <el-input
                  v-model.trim="row.price"
                  size="small"
                  :placeholder="$t('rules.mustFill', [])"
                >
                </el-input>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="action"
              :title="$t('text.action')"
              width="50"
            >
              <template v-slot="{ row, rowIndex }">
                <el-button
                  type="text"
                  size="small"
                  @click="remove(row, rowIndex)"
                  >{{ $t("text.remove") }}</el-button
                >
              </template>
            </vxe-table-column>
          </vxe-table>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="createProcureDoing"
          size="small"
          @click="createProcureSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="createProcureDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import axios from "axios";
import { page, table } from "@/utils/mixins.js";
import RawHtmlLimitNumber from "@/components/RawHtmlLimitNumber";
import Narrow from "@/components/Narrow";

import Util from "@/utils/index.js";
import SupplierMms from "@/api/supplierMms.js";
import SubjectMms from "@/api/subjectMms.js";
import Material from "@/api/material.js";
import Procure from "@/api/procure.js";

export default {
  mixins: [
    page,
    table.pager,
    table.search,
    table.narrow,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    RawHtmlLimitNumber,
    Narrow,
  },
  data() {
    return {
      pickerTodayAfterTime: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7;
        },
      },
      Util: Util,
      initFilter: null,
      searchForm: {
        serialNo: null,
        supplierId: null,
        subjectId: null,
      },
      suppliers: [],
      subjects: [],
      //
      datasAll: null,
      datas: [],
      //
      selectRecordQty: 0,
      selectRecords: [],
      createProcureDF: false,
      createProcureObj: {
        handOverAt: null,
        remark: null,
        procureItems: [],
      },
      createProcureDoing: false,
      //
      onTheWays: [],
    };
  },

  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    self.loadingStart();
    axios.all([SubjectMms.findByTenantId(), SupplierMms.findByTenantId()]).then(
      axios.spread(function (subjects, suppliers) {
        if (subjects.status === 200) {
          if (self.Permit.currentUser().admin) {
            self.subjects = subjects.data;
          } else {
            self.subjects = subjects.data.filter((s) =>
              self.Permit.mmsAuthSubjuctIds().includes(s.id)
            );
          }
        } else {
          self.sysError();
        }
        if (suppliers.status === 200) {
          self.suppliers = suppliers.data;
        } else {
          self.sysError();
        }
        self.loadingEnd();
      })
    );
  },
  methods: {
    getOnTheWays(row) {
      const self = this;
      Procure.onTheWayFindByMatierlId(row.materialId).then(function (res) {
        if (res.status === 200) {
          self.onTheWays = res.data;
        } else {
          self.sysError();
        }
      });
    },

    groupProductSuppliers(row) {
      const self = this;
      const materialSuppliers = row.materialSuppliers;
      const materialSupplierIds = row.materialSuppliers.map(
        (ms) => ms.supplierId
      );
      const otherSuppliers = self.suppliers.filter(
        (p) => !materialSupplierIds.includes(p.id)
      );
      let data = [];
      if (materialSuppliers.length != 0) {
        data.push({
          label: "物料供应商",
          suppliers: materialSuppliers,
        });
      }
      if (otherSuppliers.length != 0) {
        data.push({
          label: "其他供应商",
          suppliers: otherSuppliers.map((m) => {
            return {
              supplierId: m.id,
              supplierName: m.name,
            };
          }),
        });
      }
      if (data.length !== 0 && data.length == 1) {
        data[0].label = "";
      }
      return data;
    },
    // 生成物料采购单
    createProcure() {
      const self = this;
      if (this.selectRecords.length === 0) {
        this.$message({
          message: this.$t("prompt.atLeastOneData"),
          type: "warning",
        });
        return;
      }
      self.createProcureObj.handOverAt = null;
      self.createProcureObj.remark = null;
      self.createProcureObj.procureItems = this.selectRecords.map((c) => {
        return {
          materialId: c.materialId,
          image: c.image,
          materialSerialNo: c.serialNo,
          materialName: c.name,
          subjectId: c.subjectId,
          subjectName: c.subjectName,
          color: c.color,
          colorNo: c.colorNo,
          inUnit: c.inUnit,
          unit: c.inUnit,
          exchange: c.exchange,
          quantity: Util.toFixed((c.deficitQty * -1) / c.exchange, 4),
          price: c.inPrice == 0 ? null : c.inPrice,
          remark: null,
          supplierId:
            c.defaultSupplier != null ? c.defaultSupplier.supplierId : null,
          supplierName:
            c.defaultSupplier != null ? c.defaultSupplier.supplierName : null,
          materialSuppliers: c.materialSuppliers,
        };
      });
      self.createProcureDF = true;
    },
    remove(row, idx) {
      this.createProcureObj.procureItems.splice(idx, 1);
    },
    createProcureSubmit() {
      const self = this;
      this.$refs.createProcureForm.validate((valid) => {
        if (valid) {
          const procureItems = JSON.parse(
            JSON.stringify(this.createProcureObj.procureItems)
          ).filter(
            (item) =>
              item.quantity != null &&
              item.quantity != "" &&
              item.price != null &&
              item.price != ""
          );
          if (procureItems.length === 0) {
            self.$message({
              message: self.$t("prompt.atLeastOneValidData"),
              type: "warning",
            });
            return;
          }

          for (const procureItem of procureItems) {
            if (!Util.regexQuantity(procureItem.price)) {
              self.$message({
                message: self.$t("rules.regex", [self.$t("material.price")]),
                type: "warning",
              });
              return;
            }
            if (!Util.regexQuantity(procureItem.quantity)) {
              self.$message({
                message: self.$t("rules.regex", [self.$t("text.quantity")]),
                type: "warning",
              });
              return;
            }
            if (procureItem.supplierId != null) {
              procureItem.supplierName = self.suppliers.find(
                (s) => s.id === procureItem.supplierId
              ).name;
            }
          }
          self.createProcureObj.handOverAt =
            Util.formatDateTime(self.createProcureObj.handOverAt) + ":00";
          self.createProcureObj.procureItems = procureItems;
          self.saveDoing = true;
          Procure.create(self.createProcureObj).then(function (res) {
            if (res.status === 200) {
              self.saveDoing = false;
              self.createProcureDF = false;
              self.$message({
                message: self.$t("prompt.addSuccess"),
                type: "success",
              });
            } else {
              self.sysError();
            }
          });
        } else {
          return false;
        }
      });
    },
    //
    dataProvider(params) {
      return Material.lackStatistics(params);
    },
  },
};
</script>
